import type { LocationQuery, RouteParams, Router } from 'vue-router'

import PGPWA192 from '@/assets/icons/favicon-192.forced.png'
import PGPWA512 from '@/assets/icons/favicon-512.forced.png'
import PGPWAMaskedSVG from '@/assets/icons/favicon.svg'
import PlaygamaOG from '@/assets/icons/Playgama-OG.webp'
import { ROUTE_NAMES, type ROUTE_NAMES_VALUES } from '@/router/constants'
import type { PlatformType } from '@/types'
import { ApiRequests } from '@/utils/requests/api-requests'
import { isCountryRestricted } from '@/utils/requests/utils'

type ImageType = 'image/webp' | 'image/png' | 'image/svg+xml'
type ImagePurpose = 'any' | 'maskable'
type Display = 'fullscreen' | 'standalone'
type FormFactor = 'wide' | 'narrow'

interface ManifestIcon {
    src: string
    sizes: string
    type: ImageType
    purpose: ImagePurpose
}

interface ManifestScreenshot {
    src: string
    sizes: string
    type: ImageType
    form_factor?: FormFactor
    label?: string
}

interface RelatedApplication {
    platform: string
    url?: string
    id?: string
}

interface BaseManifest {
    name: string
    short_name: string
    id: string
    start_url: string
    display: Display
    background_color: string
    theme_color: string
    icons: ManifestIcon[]
    screenshots: ManifestScreenshot[]
    related_applications?: RelatedApplication[]
    prefer_related_applications?: boolean
}

interface GameManifest extends BaseManifest {
    display: Display
    scope: string
    description: string
    categories: string[]
    orientation: 'any'
}

// No need for a lot of icons
// Any maskable is discouraged
// You need at least one any icon
// https://web.dev/learn/pwa/web-app-manifest#for_chromium_browsers
export class ManifestGenerator {
    private static readonly CDN_REGEX = /playgama\.com\/cdn-cgi\/imagedelivery\//
    private static readonly CDN_SIZE_REGEX = /\/(thumb|enlarged)$/
    private static readonly MANIFEST_COLORS = {
        background: '#000',
        theme: '#000',
    }

    constructor(
        private readonly router: Router,
        private readonly apiRequests: ApiRequests,
    ) {}

    private static isCDNImage(url: string): boolean {
        return ManifestGenerator.CDN_REGEX.test(url)
    }

    private static removeSizeParam(url: string): string {
        return url.replace(ManifestGenerator.CDN_SIZE_REGEX, '/')
    }

    public static getScaledImage(url: string, width: number, height = width): string {
        if (!ManifestGenerator.isCDNImage(url)) {
            return url
        }

        const baseURL = ManifestGenerator.removeSizeParam(url)
        return `${baseURL}fit=cover,w=${width},h=${height}`
    }

    private getRouterPath(name: ROUTE_NAMES_VALUES, params?: RouteParams, query?: LocationQuery): string {
        const { fullPath } = this.router.resolve({
            name,
            params,
            query,
        })

        return fullPath
    }

    nativeAppManifestAddon = {
        prefer_related_applications: true,
        related_applications: [
            {
                platform: 'play',
                id: 'com.playgama.twa',
            },
        ],
        screenshots: [
            {
                src: PlaygamaOG,
                sizes: '2400x1260',
                form_factor: 'wide',
                type: 'image/webp',
                label: 'Playgama.com cover',
            },
            {
                src: PGPWA512,
                sizes: '512x512',
                type: 'image/png',
                label: 'Playgama.com cover',
            },
        ],
    }

    convertToNativeManifestIfNeeded(manifest: BaseManifest, platform?: PlatformType): GameManifest {
        if (platform === 'android') {
            return { ...manifest, ...this.nativeAppManifestAddon } as GameManifest
        }
        return manifest as GameManifest
    }

    public generateMainManifest(clid?: string, platform?: PlatformType): BaseManifest {
        const homePath = this.getRouterPath(ROUTE_NAMES.MAIN)
        const startPath = this.getRouterPath(ROUTE_NAMES.MAIN, undefined, clid ? { clid } : undefined)

        const manifest: BaseManifest = {
            name: 'Playgama.com',
            short_name: 'Playgama',
            id: homePath,
            start_url: startPath,
            display: 'standalone' as Display,
            background_color: ManifestGenerator.MANIFEST_COLORS.background,
            theme_color: ManifestGenerator.MANIFEST_COLORS.theme,
            icons: [
                {
                    src: PGPWAMaskedSVG,
                    sizes: '512x512',
                    type: 'image/svg+xml',
                    purpose: 'maskable',
                },
                {
                    src: PGPWA192,
                    sizes: '192x192',
                    type: 'image/png',
                    purpose: 'any',
                },
                {
                    src: PGPWA512,
                    sizes: '512x512',
                    type: 'image/png',
                    purpose: 'any',
                },
            ] as ManifestIcon[],
            screenshots: [],
        }

        return this.convertToNativeManifestIfNeeded(manifest, platform)
    }

    public async generateGameManifest(
        hru: string,
        clid?: string,
        platform?: PlatformType,
        { countryCode, isWebApp }: { countryCode?: string; isWebApp?: boolean } = {},
    ): Promise<{ status: 'ok'; manifest: GameManifest } | { status: 'not-found' } | { status: 'error' }> {
        const result = await this.apiRequests.getGameByParam({
            hru,
            locale: 'en-US',
            context: 'portal',
            clientType: platform === 'desktop' ? 'desktop-web' : 'mobile-web',
        })

        if (result.status === 'error') {
            return { status: 'error' }
        }

        if (result.status !== 'ok') {
            return { status: 'not-found' }
        }

        if (isCountryRestricted(result.data.game.restricted_countries, countryCode)) {
            return { status: 'not-found' }
        }

        if (platform === 'android' && !isWebApp) {
            return { status: 'ok', manifest: this.generateMainManifest(clid, platform) as GameManifest }
        }

        const { title, description, icon } = result.data.game
        const params: Record<string, string> = clid ? { clid } : {}

        const startUrl = this.getRouterPath(ROUTE_NAMES.GAME_PAGE, { game: hru }, params)
        const gamePath = this.getRouterPath(ROUTE_NAMES.GAME_PAGE, { game: hru })

        const manifest: GameManifest = {
            name: `${title} - Playgama.com`,
            short_name: title,
            id: gamePath,
            start_url: startUrl,
            scope: gamePath,
            display: 'fullscreen',
            background_color: ManifestGenerator.MANIFEST_COLORS.background,
            theme_color: ManifestGenerator.MANIFEST_COLORS.theme,
            description,
            icons: [
                {
                    src: ManifestGenerator.getScaledImage(icon, 512),
                    sizes: '512x512',
                    type: 'image/webp',
                    purpose: 'maskable',
                },
                {
                    src: ManifestGenerator.getScaledImage(icon, 384),
                    sizes: '384x384',
                    type: 'image/webp',
                    purpose: 'maskable',
                },
                {
                    src: ManifestGenerator.getScaledImage(icon, 192),
                    sizes: '192x192',
                    type: 'image/webp',
                    purpose: 'maskable',
                },
                {
                    src: ManifestGenerator.getScaledImage(icon, 512),
                    sizes: '512x512',
                    type: 'image/webp',
                    purpose: 'any',
                },
                {
                    src: ManifestGenerator.getScaledImage(icon, 384),
                    sizes: '384x384',
                    type: 'image/webp',
                    purpose: 'any',
                },
                {
                    src: ManifestGenerator.getScaledImage(icon, 192),
                    sizes: '192x192',
                    type: 'image/webp',
                    purpose: 'any',
                },
            ],
            screenshots: [],
            categories: ['games', 'entertainment'],
            orientation: 'any',
            related_applications: [],
            prefer_related_applications: true,
        }

        return { status: 'ok', manifest }
    }
}
